import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { BaseSelectOption, mapToSelectOptions } from '@utils/select.util';
import { useMemo } from 'react';
import { useFieldContext } from './form';
import { FormErrorMessage, FormField, FormLabel } from './shared.component';

type SelectFieldProps = React.ComponentProps<typeof Select> & {
  className?: string;
  clearable?: boolean;
  errorMessage?: string;
  label?: string;
  options: BaseSelectOption[];
  placeholder?: string;
  required?: boolean;
  tooltip?: React.ReactNode;
};

/**
 * A wrapper component over the basic Select that adds TanStack Form logic and validation.
 * Provides form field context, error handling, accessibility features, and options management.
 * @param props - Standard select props plus required options array and optional styling
 * @returns A form-integrated select field component with dropdown options
 */
export function SelectField(props: SelectFieldProps) {
  const {
    className,
    label,
    options,
    errorMessage,
    placeholder = 'Select an option',
    required,
    clearable = false,
    onValueChange,
    tooltip,
    ...otherProps
  } = props;
  const field = useFieldContext<string | number | undefined | null>();

  const normalizedValue =
    field.state.value === null || field.state.value === undefined
      ? ''
      : typeof field.state.value === 'number'
        ? field.state.value.toString()
        : field.state.value;

  const handleValueChange = (value: string) => {
    if (typeof field.state.value === 'number') {
      const numericValue = Number(value);
      field.handleChange(isNaN(numericValue) ? undefined : numericValue);
    } else {
      field.handleChange(value === '' || value === undefined ? null : value);
    }
    onValueChange?.(value);
  };

  const normalizedOptions = useMemo(() => mapToSelectOptions(options), [options]);

  return (
    <FormField className={className}>
      {label && (
        <FormLabel
          htmlFor={field.name}
          required={required}
          tooltip={tooltip}
        >
          {label}
        </FormLabel>
      )}

      <Select
        onValueChange={handleValueChange}
        value={normalizedValue}
        {...otherProps}
      >
        <SelectTrigger
          aria-invalid={!!errorMessage || !field.state.meta.isValid}
          className="w-full"
          clearable={clearable}
          id={field.name}
          hasError={!!errorMessage || !field.state.meta.isValid}
          hasValue={field.state.value !== undefined && field.state.value !== null}
          onClear={() => field.handleChange(null)}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {normalizedOptions.map((option) => (
            <SelectItem
              key={option.value}
              hidden={option.hidden && option.value !== normalizedValue}
              value={option.value}
            >
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <FormErrorMessage message={errorMessage} />
    </FormField>
  );
}
